import React, { useEffect, useState } from "react";
import { Event } from "@prisma/client";
import Link from "next/link";
import { GoLinkExternal } from "react-icons/go";
import {
    FaStar,
    FaStarHalfAlt,
    FaRegStar,
    FaChevronDown,
} from "react-icons/fa";
import { api } from "~/utils/api";
import { Label } from "../../shadcn/Label";
import { useFormContext } from "react-hook-form";
import { EventFormData } from "./EventFormDataType";
import { If } from "../../Conditional";
import {
    Collapsible,
    CollapsibleTrigger,
} from "~/components/shadcn/Collapsible";
import { CollapsibleContent } from "@radix-ui/react-collapsible";
import { createGoogleMapsUrl, isValidVenue } from "~/utils/event";
import { EventBySlugOutput } from "~/types/EventTypesInferred";

const VenueRecommender = ({
    event,
    onSetLocation,
}: {
    event: EventBySlugOutput;
    onSetLocation: (id: string) => void;
}) => {
    const form = useFormContext<EventFormData>();
    const latitude = form.watch("latitude");
    const longitude = form.watch("longitude");

    const [isOpen, setIsOpen] = useState(!isValidVenue(event));
    const [geoLocation, setGeoLocation] = useState<
        undefined | { latitude: number; longitude: number }
    >();
    const nearbyVenues = api.event.getNearbyVenues.useQuery({
        event: { slug: event.slug, isVirtual: !!event.isVirtual },
        geoLocation,
    });

    useEffect(() => {
        if (!latitude || !longitude) return setGeoLocation(undefined);
        setGeoLocation({ latitude, longitude });
    }, [latitude, longitude]);

    const googleMapsLink = (event: Event) =>
        createGoogleMapsUrl({
            googleMapsPlaceId: event.googleMapsPlaceId,
            googleMapsPlaceName: event.googleMapsPlaceName,
            latitude: event.latitude,
            longitude: event.longitude,
            locationName: event.locationName,
        });

    const handleSetLocation = (event: Event) => {
        if (!event.googleMapsPlaceId) return;
        form.setValue("googleMapsPlaceId", event.googleMapsPlaceId, {
            shouldDirty: true,
        });
        form.setValue("googleMapsPlaceName", event.googleMapsPlaceName, {
            shouldDirty: true,
        });
        form.setValue("googleMapsPlaceTypes", event.googleMapsPlaceTypes, {
            shouldDirty: true,
        });
        form.setValue("location", event.location, { shouldDirty: true });
        form.setValue("locationName", event.locationName, {
            shouldDirty: true,
        });
        form.setValue("useGoogleMapsLocation", event.useGoogleMapsLocation, {
            shouldDirty: true,
        });
        form.setValue("latitude", event.latitude, { shouldDirty: true });
        form.setValue("longitude", event.longitude, { shouldDirty: true });
        form.setValue("city", event.city, { shouldDirty: true });
        form.setValue("country", event.country, { shouldDirty: true });
        form.setValue("timezone", event.timezone, { shouldDirty: true });
        onSetLocation(event.googleMapsPlaceId);
    };

    if (nearbyVenues.data && nearbyVenues.data.length === 0) {
        return null;
    }

    return (
        <Collapsible
            open={isOpen}
            onOpenChange={setIsOpen}
            className="mx-3 flex flex-col gap-3 pt-2"
        >
            <CollapsibleTrigger className="group flex w-full items-center gap-2 rounded p-2 transition-all duration-200 hover:bg-gray-800">
                <FaChevronDown
                    className={`h-4 w-4 text-gray-400 transition-transform duration-200 group-hover:text-white ${
                        isOpen ? "rotate-180" : ""
                    }`}
                />
                <span className="font-medium text-gray-300 group-hover:text-white">
                    {!!geoLocation
                        ? `Recommended Venues near ${form.watch("city")}`
                        : "Recommended Venues near you"}
                </span>
            </CollapsibleTrigger>

            <CollapsibleContent>
                <div className="flex flex-col divide-y divide-gray-700 overflow-hidden rounded-lg">
                    {nearbyVenues.data?.map((venue) => (
                        <div
                            key={venue.id}
                            className="group flex items-center justify-between p-4 transition-all duration-200 hover:bg-gray-800"
                        >
                            <div
                                className="flex-grow cursor-pointer"
                                onClick={() => handleSetLocation(venue)}
                            >
                                <div className="flex items-center justify-between">
                                    <span className="text-gray-200 transition-colors duration-200 group-hover:text-white">
                                        {venue.locationName}
                                    </span>
                                    <If condition={venue.avg_rating > 0}>
                                        <div className="flex items-center gap-2">
                                            <StarRating
                                                rating={venue.avg_rating}
                                            />
                                            <span className="text-sm text-gray-500">
                                                ({venue.rating_count})
                                            </span>
                                        </div>
                                    </If>
                                </div>
                            </div>
                            <Link
                                href={googleMapsLink(venue)}
                                target="_blank"
                                className="ml-4 rounded p-2 text-gray-400 transition-all duration-200 hover:bg-gray-700 hover:text-white"
                            >
                                <GoLinkExternal className="h-4 w-4" />
                            </Link>
                        </div>
                    ))}
                </div>
            </CollapsibleContent>
        </Collapsible>
    );
};

const StarRating = ({ rating }: { rating: number }) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
        <div className="flex">
            {[...Array(fullStars)].map((_, i) => (
                <FaStar key={`full-${i}`} className="text-yellow-400" />
            ))}
            {hasHalfStar && <FaStarHalfAlt className="text-yellow-400" />}
            {[...Array(emptyStars)].map((_, i) => (
                <FaRegStar key={`empty-${i}`} className="text-yellow-400" />
            ))}
        </div>
    );
};

export default VenueRecommender;
